import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Chip, CircularProgress, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Divider, Alert, LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import { EDUGATE_API_URL } from '../config/serviceConfig';

// 雷达图标签保持完整，通过换行适配有限画布，避免用省略号隐藏维度含义。
const splitRadarLabel = (value, maxChars = 6) => {
  const chars = Array.from(String(value || ''));
  const lines = [];
  for (let index = 0; index < chars.length; index += maxChars) {
    lines.push(chars.slice(index, index + maxChars).join(''));
  }
  return lines.length ? lines : ['-'];
};

// 简易雷达图（SVG 实现）
const RadarChart = ({ dimensions, size = 300 }) => {
  const parsedDims = useMemo(() => {
    if (!dimensions) return [];
    try {
      const dimObj = typeof dimensions === 'string' ? JSON.parse(dimensions) : dimensions;
      return Object.entries(dimObj).map(([name, data]) => ({
        fullName: name,
        score: Number(data['得分'] || data.score || 0),
      }));
    } catch { return []; }
  }, [dimensions]);

  if (parsedDims.length === 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 68;
  const n = parsedDims.length;
  const angleStep = (2 * Math.PI) / n;

  // 自动检测满分
  const allScores = parsedDims.map(d => d.score);
  const maxScore = Math.max(...allScores) <= 5 ? 5 : 100;

  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const gridPaths = gridLevels.map(level => {
    const pts = Array.from({ length: n }, (_, i) => {
      const angle = i * angleStep - Math.PI / 2;
      return `${cx + maxR * level * Math.cos(angle)},${cy + maxR * level * Math.sin(angle)}`;
    });
    return pts.join(' ');
  });

  const dataPoints = parsedDims.map((d, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const r = (d.score / maxScore) * maxR;
    return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
  });

  const labels = parsedDims.map((d, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const lr = maxR + 36;
    return { ...d, x: cx + lr * Math.cos(angle), y: cy + lr * Math.sin(angle) };
  });

  return (
    <svg width={size} height={size} style={{ display: 'block', margin: '0 auto' }}>
      {gridPaths.map((pts, i) => (
        <g key={i}>
          <polygon points={pts} fill={i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.01)'} stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
          <text x={cx + 4} y={cy - maxR * gridLevels[i] + 3}
            fill="rgba(255,255,255,0.25)" fontSize={9} textAnchor="start">
            {(maxScore * gridLevels[i]).toFixed(maxScore <= 5 ? 1 : 0)}
          </text>
        </g>
      ))}
      {parsedDims.map((_, i) => {
        const angle = i * angleStep - Math.PI / 2;
        return (
          <line key={i}
            x1={cx} y1={cy}
            x2={cx + maxR * Math.cos(angle)} y2={cy + maxR * Math.sin(angle)}
            stroke="rgba(255,255,255,0.08)" strokeWidth={1}
          />
        );
      })}
      <polygon points={dataPoints.join(' ')} fill="rgba(76,175,80,0.25)" stroke="#4CAF50" strokeWidth={2} />
      {parsedDims.map((d, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const r = (d.score / maxScore) * maxR;
        return <circle key={i} cx={cx + r * Math.cos(angle)} cy={cy + r * Math.sin(angle)} r={4} fill="#4CAF50" stroke="#1a1a1a" strokeWidth={1} />;
      })}
      {labels.map((l, i) => (
        <g key={i}>
          {(() => {
            const lines = splitRadarLabel(l.fullName);
            const lineHeight = 12;
            const startY = l.y - (lines.length * lineHeight) / 2 - 3;
            return (
              <>
                <text x={l.x} y={startY} textAnchor="middle"
                  fill="rgba(255,255,255,0.7)" fontSize={10}>
                  {lines.map((line, lineIndex) => (
                    <tspan key={`${i}-${lineIndex}`} x={l.x} dy={lineIndex === 0 ? 0 : lineHeight}>
                      {line}
                    </tspan>
                  ))}
                </text>
                <text x={l.x} y={startY + lines.length * lineHeight + 4} textAnchor="middle"
                  fill="#4CAF50" fontSize={10} fontWeight="600">
                  {l.score.toFixed(1)}
                </text>
              </>
            );
          })()}
        </g>
      ))}
    </svg>
  );
};

// 等级颜色映射
const levelColor = (level) => {
  if (!level) return '#888';
  if (level.includes('优秀') || level.includes('卓越')) return '#4CAF50';
  if (level.includes('良好') || level.includes('进阶')) return '#2196F3';
  if (level.includes('基础') || level.includes('入门')) return '#FF9800';
  return '#888';
};

// 分数颜色
const scoreColor = (score) => {
  if (score >= 4.5) return '#4CAF50';
  if (score >= 4.0) return '#2196F3';
  if (score >= 3.0) return '#FF9800';
  return '#f44336';
};

const EvaluationPanel = ({ archiveId, standard, onClose, chartSize = 300 }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [expandedDims, setExpandedDims] = useState({});

  useEffect(() => {
    if (!archiveId) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ archiveId });
    if (standard) params.append('standard', standard);

    fetch(`${EDUGATE_API_URL}/api/evaluation/by-archive?${params}`)
      .then(res => res.json())
      .then(resp => {
        if (resp.code === 0 && resp.data) {
          setStatus(resp.data.status);
          setData(resp.data.evaluation || null);
        } else {
          setError(resp.message || t('eval_query_failed'));
        }
      })
      .catch(err => setError(t('eval_network_error', { error: err.message })))
      .finally(() => setLoading(false));
  }, [archiveId, standard]);

  // 解析 JSON 字段
  const parseJSON = (val) => {
    if (!val) return null;
    try { return typeof val === 'string' ? JSON.parse(val) : val; } catch { return null; }
  };

  const dimensions = parseJSON(data?.dimensions);
  const advantages = parseJSON(data?.advantages);
  const improvements = parseJSON(data?.improvements);
  const suggestions = parseJSON(data?.developmentSuggestions);
  const focusAreas = parseJSON(data?.focusAreas);

  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined || value === '') return [];
    return [value];
  };

  const renderEnhancedItem = (item, index) => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      return (
        <Box key={index} sx={{ mb: 1, p: 1.2, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.03)' }}>
          {Object.entries(item).map(([key, value]) => (
            <Typography key={key} sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, mb: 0.4, lineHeight: 1.6 }}>
              <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{key}：</Box>
              {value || '-'}
            </Typography>
          ))}
        </Box>
      );
    }
    return (
      <Typography key={index} sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 0.8, pl: 2 }}>
        • {item}
      </Typography>
    );
  };

  const hasObjectItems = (value) => toArray(value).some(item => item && typeof item === 'object' && !Array.isArray(item));

  const isAIChatLogEvaluation = Boolean(
    data?.evaluationStandard?.includes('AI IDE') ||
    data?.overallComment?.includes('协作画像') ||
    hasObjectItems(advantages) ||
    hasObjectItems(improvements) ||
    hasObjectItems(suggestions)
  );

  const toFiniteNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const formatFixed = (value, digits = 1, fallback = '0.0') => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(digits) : fallback;
  };

  const progressValue = (value, multiplier = 10) => {
    const num = toFiniteNumber(value, 0) * multiplier;
    return Math.max(0, Math.min(100, num));
  };

  // 切换维度展开/收起
  const toggleDimExpand = (dimName) => {
    setExpandedDims(prev => ({ ...prev, [dimName]: !prev[dimName] }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#4CAF50' }} />
        <Typography sx={{ ml: 2, color: '#888' }}>{t('eval_loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (status === 'not_found') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: '#888', fontSize: 14 }}>
          {t('eval_not_found')}
        </Typography>
      </Box>
    );
  }

  if (status === 'pending') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={24} sx={{ color: '#FF9800', mr: 1 }} />
        <Typography sx={{ color: '#FF9800', fontSize: 14, mt: 1 }}>
          {t('eval_pending')}
        </Typography>
      </Box>
    );
  }

  if (status === 'failed') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <WarningIcon sx={{ color: '#f44336', fontSize: 32 }} />
        <Typography sx={{ color: '#f44336', fontSize: 14, mt: 1 }}>
          {t('eval_failed')}
        </Typography>
      </Box>
    );
  }

  // 区块样式
  const sectionSx = { bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', p: 2, mb: 2 };

  return (
    <Box sx={{
      bgcolor: '#1a1a1a',
      borderRadius: '12px',
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      {/* 头部 */}
      <Box sx={{
        p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <StarIcon sx={{ color: '#FFD700', fontSize: 22 }} />
          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{t('eval_title')}</Typography>
          {data?.overallLevel && (
            <Chip label={data.overallLevel} size="small"
              sx={{ bgcolor: levelColor(data.overallLevel) + '22', color: levelColor(data.overallLevel), fontWeight: 600, fontSize: 12 }} />
          )}
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small" sx={{ color: '#888', '&:hover': { color: '#fff' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ p: 2.5 }}>

        {/* ===== 综合能力评分 ===== */}
        <Box sx={{
          ...sectionSx,
          bgcolor: 'rgba(76,175,80,0.06)',
          borderColor: 'rgba(76,175,80,0.15)',
        }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600, mb: 1.5 }}>{t('eval_overall_score')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#4CAF50', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>
                {data?.comprehensiveScore || '-'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>/5.0</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{t('eval_overall_level')}</Typography>
                <Chip label={data?.overallLevel || '-'} size="small"
                  sx={{ height: 20, fontSize: 10, bgcolor: levelColor(data?.overallLevel) + '18', color: levelColor(data?.overallLevel), fontWeight: 600 }} />
              </Box>
              {data?.totalEvidenceCount != null && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                  {t('eval_total_evidence', { count: data.totalEvidenceCount, depth: formatFixed(data.averageDepthScore, 2, '0.00') })}
                </Typography>
              )}
            </Box>
          </Box>
          {data?.overallComment && (
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '6px', p: 1.5 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, mb: 0.5, fontWeight: 600 }}>{t('eval_comprehensive')}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, lineHeight: 1.7 }}>
                {data.overallComment}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ===== 能力雷达图 ===== */}
        {dimensions && (
          <Box sx={sectionSx}>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600, mb: 1.5 }}>{t('eval_dimension_dist')}</Typography>
            <RadarChart dimensions={dimensions} size={chartSize} />
          </Box>
        )}



                {/* ===== 维度评估数据（核心区域，放在最前面） ===== */}
                {dimensions && (
          <Box sx={sectionSx}>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600, mb: 2 }}>{t('eval_dimension_data')}</Typography>
            {Object.entries(dimensions).map(([dimName, dimData]) => {
              const score = toFiniteNumber(dimData['得分'] ?? dimData.score, 0);
              const briefAnalysis = dimData['简要分析'] || dimData.briefAnalysis || '';
              const evidenceList = dimData['证据列表'] || dimData.evidenceList || [];
              const evidenceCount = dimData['证据数量'] || dimData.evidenceCount || evidenceList.length;
              const depthScore = toFiniteNumber(dimData['证据深度评分'] ?? dimData.depthScore, 0);
              const isExpanded = expandedDims[dimName];

              return (
                <Box key={dimName} sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                  {/* 维度头部 */}
                  <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{dimName}</Typography>
                        <Chip label={`${formatFixed(score, 1)}${t('eval_score_unit')}`} size="small"
                          sx={{ height: 20, fontSize: 10, bgcolor: scoreColor(score) + '18', color: scoreColor(score), fontWeight: 600 }} />
                      </Box>
                      {briefAnalysis && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, lineHeight: 1.6 }}>
                          {briefAnalysis}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{t('eval_depth')}: {formatFixed(depthScore, 2, '0.00')}</Typography>
                    </Box>
                  </Box>

                  {/* 证据展开/收起按钮 */}
                  {evidenceList.length > 0 && (
                    <Box
                      onClick={() => toggleDimExpand(dimName)}
                      sx={{
                        px: 1.5, py: 0.8, bgcolor: 'rgba(255,255,255,0.02)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        borderTop: '1px solid rgba(255,255,255,0.04)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
                      }}
                    >
                      <ExpandMoreIcon sx={{
                        color: '#2196F3', fontSize: 16,
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }} />
                      <Typography sx={{ color: '#2196F3', fontSize: 11 }}>
                        {isExpanded ? t('eval_collapse_evidence') : t('eval_view_evidence')} ({evidenceCount})
                      </Typography>
                    </Box>
                  )}

                  {/* 证据列表 */}
                  {isExpanded && evidenceList.length > 0 && (
                    <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
                      {evidenceList.map((ev, j) => {
                        const content = ev['内容'] || ev.content || '';
                        const lineNo = ev['行号'] || ev.lineNumber || ev.line || '';
                        const specificity = toFiniteNumber(ev['具体性评分'] ?? ev.specificity, 0);
                        const relevance = toFiniteNumber(ev['相关性评分'] ?? ev.relevance, 0);

                        return (
                          <Box key={j} sx={{
                            mb: 1.5, p: 1.2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '6px',
                            borderLeft: '3px solid rgba(76,175,80,0.3)'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.8 }}>
                              <Chip label={t('eval_evidence_number', { number: j + 1 })} size="small"
                                sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(33,150,243,0.1)', color: '#2196F3' }} />
                              {lineNo && (
                                <Chip label={t('eval_line_number', { number: lineNo })} size="small" variant="outlined"
                                  sx={{ height: 18, fontSize: 9, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)' }} />
                              )}
                            </Box>
                            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, lineHeight: 1.6, mb: 1 }}>
                              {content}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, minWidth: 35 }}>{t('eval_specificity')}</Typography>
                                <LinearProgress variant="determinate" value={progressValue(specificity)}
                                  sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)', maxWidth: 80, '& .MuiLinearProgress-bar': { bgcolor: '#2196F3', borderRadius: 2 } }} />
                                <Typography sx={{ color: '#2196F3', fontSize: 10, fontWeight: 600, minWidth: 25 }}>{formatFixed(progressValue(specificity), 1)}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, minWidth: 35 }}>{t('eval_relevance')}</Typography>
                                <LinearProgress variant="determinate" value={progressValue(relevance)}
                                  sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)', maxWidth: 80, '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50', borderRadius: 2 } }} />
                                <Typography sx={{ color: '#4CAF50', fontSize: 10, fontWeight: 600, minWidth: 25 }}>{formatFixed(progressValue(relevance), 1)}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* ===== 教师评定 ===== */}
        {(data?.teacherScore != null || data?.teacherComment) && (
          <Box sx={sectionSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <PersonIcon sx={{ color: '#9C27B0', fontSize: 16 }} />
              <Typography sx={{ color: '#9C27B0', fontSize: 13, fontWeight: 600 }}>{t('eval_teacher_assessment')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: toFiniteNumber(data?.teacherScore, 0) > 0 ? '#9C27B0' : 'rgba(255,255,255,0.3)', fontSize: 24, fontWeight: 700 }}>
                  {formatFixed(data?.teacherScore, 1)}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>/5.0</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, mb: 0.5 }}>{t('eval_teacher_comment')}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontStyle: data?.teacherComment ? 'normal' : 'italic' }}>
                  {data?.teacherComment || t('eval_no_teacher_comment')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* ===== 专业成长阶段 ===== */}
        {data?.growthStage && (
          <Box sx={sectionSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <SchoolIcon sx={{ color: '#00BCD4', fontSize: 16 }} />
              <Typography sx={{ color: '#00BCD4', fontSize: 13, fontWeight: 600 }}>{t('eval_professional_stage')}</Typography>
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, lineHeight: 1.7 }}>
              {data.growthStage}
            </Typography>
          </Box>
        )}

        {/* ===== 专业重点关注领域 ===== */}
        {focusAreas && Array.isArray(focusAreas) && focusAreas.length > 0 && (
          <Box sx={sectionSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AssignmentIcon sx={{ color: '#FF5722', fontSize: 16 }} />
              <Typography sx={{ color: '#FF5722', fontSize: 13, fontWeight: 600 }}>{t('eval_focus_areas')}</Typography>
            </Box>
            {focusAreas.map((item, i) => (
              <Typography key={i} sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 0.5, pl: 2 }}>
                • {typeof item === 'string' ? item : item.description || JSON.stringify(item)}
              </Typography>
            ))}
          </Box>
        )}

        {/* ===== 优势与不足 / AI 协作案例 ===== */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {toArray(advantages).length > 0 && (
            <Box sx={{ flex: 1, minWidth: 200, ...sectionSx, mb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                <Typography sx={{ color: '#4CAF50', fontSize: 13, fontWeight: 600 }}>
                  {isAIChatLogEvaluation ? '典型正面案例' : t('eval_advantages')}
                </Typography>
              </Box>
              {toArray(advantages).map(renderEnhancedItem)}
            </Box>
          )}
          {toArray(improvements).length > 0 && (
            <Box sx={{ flex: 1, minWidth: 200, ...sectionSx, mb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#FF9800', fontSize: 16 }} />
                <Typography sx={{ color: '#FF9800', fontSize: 13, fontWeight: 600 }}>
                  {isAIChatLogEvaluation ? '典型风险案例' : t('eval_improvements')}
                </Typography>
              </Box>
              {toArray(improvements).map(renderEnhancedItem)}
            </Box>
          )}
        </Box>

        {/* ===== 关键改进建议 ===== */}
        {toArray(suggestions).length > 0 && (
          <Box sx={sectionSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <LightbulbIcon sx={{ color: '#2196F3', fontSize: 16 }} />
              <Typography sx={{ color: '#2196F3', fontSize: 13, fontWeight: 600 }}>
                {isAIChatLogEvaluation ? '关键改进建议' : t('eval_development_suggestions')}
              </Typography>
            </Box>
            {toArray(suggestions).map(renderEnhancedItem)}
          </Box>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 2 }} />

        {/* ===== 元数据信息 ===== */}
        <Box sx={{ ...sectionSx, bgcolor: 'rgba(255,255,255,0.01)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <DescriptionIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 600 }}>{t('eval_metadata')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {data?.projectName && (
              <Box sx={{ minWidth: 120 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{t('eval_project_name')}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{data.projectName}</Typography>
              </Box>
            )}
            {data?.totalEvidenceCount != null && (
              <Box sx={{ minWidth: 80 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{t('eval_total_evidence_count')}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{data.totalEvidenceCount}</Typography>
              </Box>
            )}
            {data?.averageDepthScore != null && (
              <Box sx={{ minWidth: 100 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{t('eval_avg_depth_score')}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{formatFixed(data.averageDepthScore, 4, '0.0000')}</Typography>
              </Box>
            )}
            {data?.archiveId && (
              <Box sx={{ minWidth: 180 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{t('eval_archive_id')}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontFamily: 'monospace' }}>{data.archiveId}</Typography>
              </Box>
            )}
            {data?.sessionId && (
              <Box sx={{ minWidth: 220 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{t('eval_session_id')}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontFamily: 'monospace' }}>{data.sessionId}</Typography>
              </Box>
            )}
            {data?.archiveName && (
              <Box sx={{ minWidth: 220 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{t('eval_archive_name')}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontFamily: 'monospace' }}>{data.archiveName}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EvaluationPanel;
